import React from 'react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import type { ConferenciaNfeItem } from '../../types/conferencia';
import { qtd as fmtQtd } from '../../utils/format';
import { calcularContagemMultiplicada } from '../../utils/multiplicadorCaixa';
import FotoProduto from './FotoProduto';

interface UltimaLeituraCardProps {
  /** Item bipado (vivo — o workspace passa `itens.find`). */
  item: ConferenciaNfeItem;
  /** Unidades da embalagem lida (o `−/+` anda de embalagem em embalagem). */
  unidadesPorPacote: number;
  unidade: string | null;
  /** Contagem do item ANTES desta leitura — base do total projetado. */
  baseAntes: number;
  /** Multiplicador atual desta leitura. */
  mult: number;
  /** `conferencia.multiplicadorAtivo` — sem ele, o card mostra só ajuste por embalagem (sem chips ×N). */
  mostrarChips: boolean;
  onMult: (novoMult: number) => void;
}

/**
 * Card "última leitura" (só mobile) — mostra logo abaixo da câmera compacta o item que acabou
 * de ser bipado, com a quantidade editável de forma fluida. Resolve os quatro pontos do
 * feedback da 2.21.0: câmera menor, ver o item bipado, multiplicar fácil, sem disputar a tela.
 *
 * O número exibido é o TOTAL PROJETADO (`base + mult × pacote`), calculado na hora — não o
 * `item.qtdConferida`, que só atualiza depois do commit debounced (~500ms). Assim cada toque
 * mexe o número imediatamente; a gravação segue atrás.
 */
const UltimaLeituraCard: React.FC<UltimaLeituraCardProps> = ({
  item, unidadesPorPacote, unidade, baseAntes, mult, mostrarChips, onMult,
}) => {
  const total = calcularContagemMultiplicada(baseAntes, mult, unidadesPorPacote);
  // Inclui avaria pra bater com o limiar de excedente do commit (aplicarLimiteConferencia).
  const excedeu = total + item.qtdAvariada > item.qtdNota;
  const descricao = item.descricao || 'Produto não identificado';
  const unLabel = unidade ?? 'un';

  return (
    <div className="space-y-3 rounded-iris border-2 border-accent bg-surface p-3">
      <div className="flex items-start gap-2.5">
        <FotoProduto codigo={item.produto} descricao={descricao} tamanho={40} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-tx">{descricao}</p>
          <p className="mt-0.5 text-[11px] text-tx-3">
            {item.produto && <span className="font-mono">{item.produto}</span>}
            {unidadesPorPacote > 1 && ` · emb. de ${fmtQtd(unidadesPorPacote)}`}
            {` · nota ${fmtQtd(item.qtdNota)} ${unLabel}`}
          </p>
        </div>
        <span className="flex-shrink-0 rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-medium text-accent-text">
          última leitura
        </span>
      </div>

      <div className="flex items-center justify-center gap-4">
        <button
          type="button"
          aria-label="Diminuir"
          disabled={mult <= 1}
          onClick={() => onMult(mult - 1)}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-iris-sm border border-line-strong text-tx-2 hover:bg-surface-2 disabled:opacity-40"
        >
          <MinusIcon className="h-5 w-5" />
        </button>
        <div className="min-w-[5rem] text-center">
          <div className={`text-3xl font-bold leading-none tabular-nums ${excedeu ? 'text-warning' : 'text-tx'}`}>
            {fmtQtd(total)}
          </div>
          <div className="mt-1 text-[11px] text-tx-3">de {fmtQtd(item.qtdNota)} {unLabel}</div>
        </div>
        <button
          type="button"
          aria-label="Aumentar"
          onClick={() => onMult(mult + 1)}
          className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-iris-sm border border-line-strong text-tx-2 hover:bg-surface-2"
        >
          <PlusIcon className="h-5 w-5" />
        </button>
      </div>

      {mostrarChips && (
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-medium uppercase tracking-wide text-tx-3">multiplicar</span>
          {[6, 12, 24].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onMult(n)}
              aria-pressed={mult === n}
              className={`h-9 flex-1 rounded-iris-sm border text-sm font-semibold tabular-nums transition-colors ${
                mult === n
                  ? 'border-accent bg-accent text-white'
                  : 'border-line-strong bg-surface text-tx-2 hover:bg-surface-2'
              }`}
            >
              ×{n}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default UltimaLeituraCard;
